/**
 * ステータス確認モーダル（Task 14）。
 *
 * 「面談済」「フォロー（不参加）」のいずれかを選んだときに出す確認画面。
 * - completed (面談済):   お礼 LINE 自動送信予告（Worker 側で thanks_pushed 結果が返る）
 * - no_show (フォロー):   顧客通知なし（Worker 側で Push しない・社内記録のみ）
 *
 * 確定 → POST /api/admin/reservations/:id/status
 */

import { updateStatus } from "../api";
import { showToast } from "../render";
import type { Reservation } from "../types";

type Action = "completed" | "no_show";

const ACTION_META: Record<Action, {
  title: string;
  confirmLabel: string;
  confirmClass: string;
  description: string;
  notice: string;
}> = {
  completed: {
    title: "「面談済」にします",
    confirmLabel: "面談済にする",
    confirmClass: "bg-blue-600 text-white",
    description: "ご相談が無事に終わったときに使ってください。",
    notice: "お客様の LINE に自動でお礼メッセージが送信されます。",
  },
  no_show: {
    title: "「フォロー」にします",
    confirmLabel: "フォローにする",
    confirmClass: "bg-orange-500 text-white",
    description: "予定の時間にお客様が来なかったときに使ってください。後日のフォローアップ対象になります。",
    notice: "お客様への通知は送信されません（社内記録のみ）。",
  },
};

/**
 * ステータス変更モーダル。確定 → 成功時に onDone() を呼ぶ。
 */
export function openStatusModal(r: Reservation, action: Action, onDone: () => void): void {
  closeModal();

  const meta = ACTION_META[action];

  const wrap = document.createElement("div");
  wrap.className = "modal-backdrop";
  wrap.id = "modal-status";
  wrap.innerHTML = `
    <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="status-title">
      <div class="flex items-start justify-between mb-3">
        <h2 id="status-title" class="text-lg font-bold text-gray-900">${esc(meta.title)}</h2>
        <button
          id="status-close"
          aria-label="閉じる"
          class="tap-target text-gray-500 hover:text-gray-700 px-2">✕</button>
      </div>

      <div class="text-sm text-gray-700 mb-3">
        <span class="font-semibold">${esc(r.customer_name)}</span> さま<br>
        ${esc(formatYmd(r.reservation_date))} ${esc(r.reservation_time)}
      </div>

      <p class="text-sm text-gray-800 mb-3 leading-relaxed">${esc(meta.description)}</p>

      <div class="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-xs text-gray-700 leading-relaxed">
        ${esc(meta.notice)}
      </div>

      <div class="flex gap-2">
        <button
          id="status-back"
          class="tap-target flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold">
          戻る
        </button>
        <button
          id="status-submit"
          class="tap-target flex-1 px-4 py-3 ${meta.confirmClass} rounded-lg font-semibold">
          ${esc(meta.confirmLabel)}
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(wrap);

  wrap.addEventListener("click", (ev) => {
    if (ev.target === wrap) closeModal();
  });
  document.getElementById("status-close")?.addEventListener("click", () => closeModal());
  document.getElementById("status-back")?.addEventListener("click", () => closeModal());

  document.getElementById("status-submit")?.addEventListener("click", async () => {
    const btn = document.getElementById("status-submit") as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = "実行中..."; }
    try {
      const result = await updateStatus(r.id, action);
      closeModal();
      if (action === "completed") {
        if (result.thanks_pushed) {
          showToast("面談済にしました。お礼メッセージを送信しました", "success");
        } else {
          showToast("面談済にしました。ただしお礼メッセージの送信に失敗しました", "warning", 5500);
        }
      } else {
        showToast("フォローにしました", "success");
      }
      onDone();
    } catch (e: any) {
      showToast(`更新失敗：${e?.message ?? e}`, "error", 5000);
      if (btn) {
        btn.disabled = false;
        btn.textContent = ACTION_META[action].confirmLabel;
      }
    }
  });
}

function closeModal(): void {
  document.getElementById("modal-status")?.remove();
}

function esc(s: string | null | undefined): string {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatYmd(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[1]}/${Number(m[2])}/${Number(m[3])}`;
}
